import { useEffect, useCallback, useMemo } from 'react';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { LabelSuggestion } from '@atlassian/jira-issue-shared-types/src/common/types/labels-type.tsx';
import debouncePromise from '@atlassian/jira-platform-debounce-promise/src/ui/debounced-promise/index.tsx';
import { useLabelsStore } from './store/index.tsx';

type UseLabelsReturn = {
	defaultSuggestions: LabelSuggestion[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	getSuggestions: (query: string) => any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	addDefaultSuggestion: (label: string) => any;
};

const LABEL_SUGGESTIONS_DEBOUNCE_MS = 500;

export const useLabels = (issueId: string, fieldKey: string): UseLabelsReturn => {
	const [
		{ defaultSuggestions, defaultSuggestionsIssueId },
		{ setDefaultSuggestions, addDefaultSuggestion, setDefaultSuggestionsIssueId },
	] = useLabelsStore(issueId);

	if (!defaultSuggestionsIssueId) {
		setDefaultSuggestionsIssueId(issueId);
	}

	const autoCompleteUrl = useMemo(() => {
		let url = `/rest/api/1.0/labels/${issueId}/suggest?`;
		if (fieldKey !== 'labels') {
			url += `customFieldId=${fieldKey.split('_').reverse()[0]}&`;
		}
		url += 'query=';
		return url;
	}, [fieldKey, issueId]);

	const loadSuggestions = useCallback(
		(query: string) => {
			const url = `${autoCompleteUrl}${encodeURIComponent(query)}`;
			return performGetRequest(url).then((response) => response.suggestions);
		},
		[autoCompleteUrl],
	);

	const getSuggestions = useCallback(
		(query: string) => {
			if (!query && defaultSuggestions) {
				return Promise.resolve(defaultSuggestions);
			}
			return loadSuggestions(query);
		},
		[defaultSuggestions, loadSuggestions],
	);

	useEffect(() => {
		if (defaultSuggestionsIssueId === issueId && !defaultSuggestions) {
			loadSuggestions('').then((suggestions) => {
				setDefaultSuggestions(suggestions);
			});
		}
	}, [
		defaultSuggestionsIssueId,
		defaultSuggestions,
		issueId,
		loadSuggestions,
		setDefaultSuggestions,
	]);

	return useMemo(
		() => ({
			defaultSuggestions: defaultSuggestions ?? [],
			getSuggestions: debouncePromise(getSuggestions, LABEL_SUGGESTIONS_DEBOUNCE_MS),
			addDefaultSuggestion,
		}),
		[defaultSuggestions, getSuggestions, addDefaultSuggestion],
	);
};
